import type { DistanceUnit } from "@/models/siteUnit.types";

export const dtmOrthoRoles = { image: "image", terrain: "terrain" } as const;
export type DtmOrthoRole = (typeof dtmOrthoRoles)[keyof typeof dtmOrthoRoles];

/** 画像に割り当てたファイルの選択順インデックス。もう一方が地形になる（AC-6） */
export type ImageFileIndex = 0 | 1;

/** 種別選択ダイアログが表示に使う最小形。ダイアログは名前以外を読まない（GI-13） */
export type NamedFile = Readonly<{ name: string }>;

/** 外部ファイルストレージ上のファイルを指す識別情報。実体を伴わない（GI-11） */
export interface StorageNodeRef {
	readonly bucketId: string;
	readonly nodeId: string;
	/** ファイル名。Dashboard ではマスキング後の表示名（GI-10 / GC-12） */
	readonly name: string;
}

/**
 * ストレージのノード（Groupware ピッカーの要素 / Dashboard の応答）をサーバに渡すノード参照に写す。
 * バケット ID は要素ごとに持たせる（組の 2 件が別バケットにあり得る。GI-36）。id / name 以外の項目は落とす
 */
export const toStorageNodeRef = (
	bucketId: string,
	node: Readonly<{ id: string; name: string }>,
): StorageNodeRef => ({ bucketId, nodeId: node.id, name: node.name });

/** One file passed to the v2 import (`POST /v2/api/:siteId/dtm-ortho`) */
export interface DtmOrthoImportFile {
	/** StorageNodeRef.name (Dashboard: masked display name) */
	readonly name: string;
	readonly source: {
		readonly type: "ebPlatformNode";
		readonly bucketId: string;
		readonly nodeId: string;
	};
}

/** Request body of `POST /v2/api/:siteId/dtm-ortho` */
export interface DtmOrthoImportBody {
	readonly terrain: DtmOrthoImportFile;
	readonly image: DtmOrthoImportFile;
	/** YYYY-MM-DD */
	readonly date: string;
	/** HH:mm:ss */
	readonly time: string;
	/** Import unit in the v2 API spelling (toApiDistanceUnit of the selected unit) */
	readonly distanceUnit: "METERS" | "FEET" | "US_SURVEY_FEET";
}

/** Accepted response. Both rows are tracked as processing (VM-2 / VM-24) */
export interface DtmOrthoImportAccepted {
	/** point_cloud.point_cloud_id (PENDING) */
	readonly pointCloudId: number;
	/** drawing_2d.id (processing) */
	readonly drawing2dImageId: number;
}

/**
 * 取込対象の 2 ファイル。origin で実体の在り処が決まる。
 * 「片方が端末上、片方がストレージ上」という組は表現できない（GI-11）
 */
export type DtmOrthoFilePair =
	| { readonly origin: "local"; readonly files: readonly [File, File] }
	| {
			readonly origin: "storage";
			readonly files: readonly [StorageNodeRef, StorageNodeRef];
	  };

/** 種別割り当ての確定結果。origin は組から引き継ぐ（GI-4） */
export type DtmOrthoAssignment =
	| {
			readonly origin: "local";
			/** オルソ -> 二次元図面として登録 */
			readonly imageFile: File;
			/** DTM -> 測量データとして登録 */
			readonly terrainFile: File;
	  }
	| {
			readonly origin: "storage";
			readonly imageFile: StorageNodeRef;
			readonly terrainFile: StorageNodeRef;
	  };

/**
 * 種別選択ダイアログが emit する割り当て。imageFile / terrainFile は props.files の要素そのもの。
 * ダイアログは origin を知らないため型は NamedFile で、origin 付きへの復元は facade が行う（GI-4）
 */
export interface DtmOrthoRoleAssignment {
	readonly imageFile: NamedFile;
	readonly terrainFile: NamedFile;
}

/**
 * 取込フローの進行状態。facade が保持し、AddDataDialog はこれで表示するダイアログを決める。
 * client は GeoTIFF を解析しない（C-13）ため、検査のための状態は持たない。
 */
export type DtmOrthoUploadPhase =
	| "idle"
	/** 種別選択ダイアログ表示中（AC-5） */
	| "assigning"
	/** Node resolution (PUT for local files only) -> v2 import request */
	| "uploading"
	| "done"
	| "failed";

/**
 * 種別選択ダイアログの確定結果（emit の形は現行どおり。GI-28）。
 * 取込単位は 2 ファイル共通の 1 値で、ファイルごとの単位は型として表現できない（AC-46 / AC-51）
 */
export interface DtmOrthoImportSelection {
	readonly assignment: DtmOrthoRoleAssignment;
	/** 取込単位。ユーザーが選んだ値だけを使い、ファイルや現場設定から自動判定しない（C-16） */
	readonly distanceUnit: DistanceUnit;
}

/** 取込の確定入力。importDtmOrthoPair の引数（3 経路共通）。assignment は origin 付き */
export interface DtmOrthoImportInput {
	readonly assignment: DtmOrthoAssignment;
	readonly distanceUnit: DistanceUnit;
}
